import { FieldInfo } from '@/components/FieldInfo';
import { LOGIN_POST_URL } from '@/pages/Login/loginUrl';
import { useLoginForm } from '@/pages/Login/useLoginForm';
import NautWordmarkBlue from '@nautilusbio/nb-design-system/assets/nautilus-wordmark-blue.svg';
import NautWordmarkWhite from '@nautilusbio/nb-design-system/assets/nautilus-wordmark-white.svg';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { Loader } from 'lucide-react';
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.css';

const LoginForm: FC = () => {
  const { t } = useTranslation('login');
  const { form, handleForgotPassword, isSubmitted, markSubmitted, mode, toggleMode } = useLoginForm();

  const logoAlt = t('alt', {
    ns: 'logo',
  });

  const logoImageProps = {
    title: logoAlt,
  };

  return (
    <form action={LOGIN_POST_URL} className={styles.form} id="login_form" method="POST" onSubmit={markSubmitted}>
      <Stack alignItems="center" fullWidth gap={4} orientation="vertical">
        {/* Rendered visually-hidden, not dropped — screen-reader users still
            need a page-identity announcement and h1 despite the logo-only design. */}
        <Text as="h1" className={styles.visuallyHidden}>
          {t('form.heading')}
        </Text>
        <Text as="p" className={styles.visuallyHidden}>
          {t('form.description')}
        </Text>

        {/* Both variants render unconditionally — html[data-theme] CSS alone
            (styles.module.css) picks which is visible, no JS logic. */}
        <img
          {...logoImageProps}
          alt={logoAlt}
          className={styles.logoLight}
          data-testid="logo-light"
          src={NautWordmarkBlue}
        />
        <img
          {...logoImageProps}
          alt={logoAlt}
          className={styles.logoDark}
          data-testid="logo-dark"
          src={NautWordmarkWhite}
        />

        <form.Field name="email">
          {field => (
            <Stack className={styles.inputContainer} fullWidth orientation="vertical">
              <Input
                autoComplete="email"
                className={styles.inputText}
                data-testid="input-email"
                fullWidth
                id={field.name}
                label={{ children: t('form.labels.email'), htmlFor: field.name, id: 'label-email' }}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={e => field.handleChange(e.target.value)}
              />
              <FieldInfo field={field} />
            </Stack>
          )}
        </form.Field>

        {mode === 'password' && (
          <form.Field name="password">
            {field => (
              <Stack className={styles.inputContainer} fullWidth orientation="vertical">
                <Input
                  autoComplete="current-password"
                  /* eslint-disable-next-line jsx-a11y/no-autofocus */
                  autoFocus
                  className={styles.inputText}
                  data-testid="input-pass"
                  fullWidth
                  id={field.name}
                  label={{ children: t('form.labels.pass'), htmlFor: field.name, id: 'label-pass' }}
                  name={field.name}
                  showPasswordTooltipText={t('form.labels.passTooltip')}
                  type="password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                />
                <FieldInfo field={field} />
              </Stack>
            )}
          </form.Field>
        )}

        <Stack alignItems="center" fullWidth gap={1} orientation="horizontal">
          {/* No aria-label: it must match visible text (WCAG 2.5.3) — a
              mismatched one previously announced "Login" over "Login with SSO". */}
          <Button
            data-testid={mode === 'password' ? 'sso-mode-btn' : 'show-password-btn'}
            fullWidth
            onPress={toggleMode}
            type="button"
            visual="unstyled"
          >
            {mode === 'password' ? t('buttons.loginsso') : t('buttons.use_password')}
          </Button>

          {mode === 'password' && (
            <form.Subscribe
              selector={state => [state.values.email, state.fieldMeta.email?.errors?.length ?? 0] as const}
            >
              {([email, emailErrorCount]) => {
                const emailValue = email.trim();
                const isEmailValid = emailErrorCount === 0 && !!emailValue;

                return (
                  <Tooltip
                    content={t('form.labels.valid_email_required')}
                    isDisabled={isEmailValid}
                    placement="right"
                    trigger={
                      <Button
                        className={styles.forgotPasswordLink}
                        data-testid="forgot-password-link"
                        fullWidth
                        isDisabled={!isEmailValid}
                        onPress={() => handleForgotPassword(emailValue)}
                        type="button"
                        visual="unstyled"
                      >
                        {t('form.labels.forgot_password')}
                      </Button>
                    }
                  />
                );
              }}
            </form.Subscribe>
          )}
        </Stack>
      </Stack>

      <Stack className={styles.footer} gap={4}>
        <form.Subscribe
          selector={state =>
            [state.values.email, state.values.password, state.fieldMeta.email?.errors?.length ?? 0] as const
          }
        >
          {([email, password, emailErrorCount]) => {
            const isEmailValid = emailErrorCount === 0 && !!email.trim();
            const isPasswordValid = mode !== 'password' || password.length >= 1;
            const canLogin = isEmailValid && isPasswordValid;

            return (
              <Button
                data-testid="submit-btn"
                fullWidth
                isDisabled={!canLogin || isSubmitted}
                size="xl"
                type="submit"
                visual={isSubmitted ? 'success' : 'primary'}
              >
                {isSubmitted ? (
                  <>
                    <Loader className={styles.spinner} /> &nbsp;{t('logging_in', { ns: 'general' })}
                  </>
                ) : (
                  t('buttons.login')
                )}
              </Button>
            );
          }}
        </form.Subscribe>
      </Stack>
    </form>
  );
};

export default LoginForm;
